/**
 * Recently Viewed slider spacing patch
 *
 * The theme initializes the "Recently Viewed" slider with Swiper and uses `spaceBetween`
 * (which manifests as inline `margin-right` on `.swiper-slide`).
 *
 * CSS overrides can break Swiper's width calculations, so we enforce spacing via JS only.
 * This script finds the Recently Viewed slider instance and forces `spaceBetween = 10`.
 */

(() => {
  const SPACE_BETWEEN = 10;
  const MOBILE_BREAKPOINT = 767; // below this width, cards should be ~80%
  const MOBILE_SLIDES_PER_VIEW = 1.38; // 1 / 0.8 = 1.25 => each slide ~80% width
  const MAX_TRIES = 600; // ~10s at 60fps

  function patchSwiper(swiper) {
    if (!swiper || swiper.destroyed) return false;

    try {
      swiper.params = swiper.params || {};
      swiper.originalParams = swiper.originalParams || {};

      // Persist originals so we can restore on desktop.
      if (typeof swiper.__rvOriginalSlidesPerView === 'undefined') {
        swiper.__rvOriginalSlidesPerView =
          swiper.originalParams.slidesPerView ?? swiper.params.slidesPerView ?? 1;
      }

      swiper.params.spaceBetween = SPACE_BETWEEN;
      swiper.originalParams.spaceBetween = SPACE_BETWEEN;

      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      const desiredSlidesPerView = isMobile
        ? MOBILE_SLIDES_PER_VIEW
        : swiper.__rvOriginalSlidesPerView;

      swiper.params.slidesPerView = desiredSlidesPerView;
      swiper.originalParams.slidesPerView = desiredSlidesPerView;

      const applyBreakpoints = (obj) => {
        if (!obj || !obj.breakpoints) return;
        Object.keys(obj.breakpoints).forEach((bp) => {
          obj.breakpoints[bp] = obj.breakpoints[bp] || {};
          obj.breakpoints[bp].spaceBetween = SPACE_BETWEEN;
        });
      };

      applyBreakpoints(swiper.params);
      applyBreakpoints(swiper.originalParams);

      // Force recalculation of slide margins/widths.
      if (typeof swiper.updateSlides === 'function') swiper.updateSlides();
      if (typeof swiper.update === 'function') swiper.update();

      // Toggle arrows based on slide count
      const nav = swiper.el.closest('recently-viewed')?.querySelector('.recently-viewed-slider__nav');

      if (nav) {
        const isMobile = window.innerWidth <= 767;
        const limit = isMobile ? 1 : 4;

        if (swiper.slides.length <= limit) {
          nav.style.display = 'none';
        } else {
          nav.style.display = 'flex';
        }
      }

      return true;
    } catch (e) {
      console.error('[recently-viewed-space-between] patch failed', e);
      return false;
    }
  }

  function getRecentlyViewedSliderElements(root = document) {
    // Recently Viewed markup:
    // <recently-viewed class="recently-viewed-wrapper ...">
    //   <div class="swiper-container collection-slider"> ... </div>
    return Array.from(
      root.querySelectorAll('recently-viewed .swiper-container.collection-slider')
    );
  }

  function ensurePatchedForEl(sliderEl) {
    let tries = 0;

    const tick = () => {
      tries += 1;

      // Swiper instance is attached to the container element as `el.swiper`.
      const swiper = sliderEl && sliderEl.swiper;
      if (swiper && patchSwiper(swiper)) return;

      if (tries < MAX_TRIES) requestAnimationFrame(tick);
    };

    tick();
  }

  function observeRecentlyViewed(host) {
    const sliderEl = host.querySelector('.swiper-container.collection-slider');
    if (!sliderEl) return;

    // Patch as soon as possible.
    ensurePatchedForEl(sliderEl);

    // When slides are appended async, patch again (and update).
    const grid = host.querySelector('.recently-viewed-grid');
    if (grid && 'MutationObserver' in window) {
      const mo = new MutationObserver(() => ensurePatchedForEl(sliderEl));
      mo.observe(grid, { childList: true });
    }

    // If the section is hidden initially and then revealed, Swiper may recompute spacing.
    if ('MutationObserver' in window) {
      const moAttrs = new MutationObserver(() => ensurePatchedForEl(sliderEl));
      moAttrs.observe(host, { attributes: true, attributeFilter: ['class', 'style', 'open'] });
    }
  }

  function init() {
    document.querySelectorAll('recently-viewed').forEach(observeRecentlyViewed);

    // If the element is injected later, observe DOM.
    if ('MutationObserver' in window) {
      const mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes || []) {
            if (!(node instanceof Element)) continue;

            if (node.matches && node.matches('recently-viewed')) {
              observeRecentlyViewed(node);
              continue;
            }

            // Also handle fragments containing the element.
            const nested = node.querySelectorAll ? node.querySelectorAll('recently-viewed') : [];
            nested.forEach(observeRecentlyViewed);
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }

    // Safety: on resize, re-patch any existing instances.
    window.addEventListener(
      'resize',
      () => {
        getRecentlyViewedSliderElements().forEach((sliderEl) => {
          if (sliderEl.swiper) patchSwiper(sliderEl.swiper);
        });
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

